import React, { useEffect, useState } from 'react'
import cx from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import WhatsappLineIcon from 'remixicon-react/WhatsappLineIcon'
import CustomerServiceLineIcon from 'remixicon-react/CustomerServiceLineIcon'
import QuestionLineIcon from 'remixicon-react/QuestionLineIcon'
import { rootActions } from '@app-store/slices'
import { whatsappNumberSupport } from '@features/General/constants'
import styles from './index.scss'
import { RootState } from '@store/store'
import Modal from '@UI/antd/Modal'
import Preview from '@components/Tour/Preview'
import { COOKIE_KEYS, getCookie } from '@features/General'

interface Props {
    isStepLayout?: boolean
    isTokenRoute?: boolean
}
const FloatingIcons = ({ isStepLayout, isTokenRoute }: Props) => {
    const dispatch = useDispatch()
    const eventId = getCookie(COOKIE_KEYS.ALA_EVENT)
    const { data: tourData } = useSelector((state: RootState) => state.tours.tourByEndpoint)
    const { status: tourPreferenceStatus } = useSelector(
        (state: RootState) => state.companies.preferenceCustomizationList
    )

    const [tourPreferenceData, setTourPreferenceData] = useState(null)
    const [viewHelpIcon, setViewHelpIcon] = useState(false)
    const [viewTourModal, setViewTourModal] = useState(false)

    const currentPath = window.location.pathname

    useEffect(() => {
        if (!isTokenRoute && eventId) {
            dispatch(
                rootActions.companies.preferenceCustomizationList.onRequest({
                    query: { module: 'tours' },
                    limit: 1,
                    noSaveInStore: true,
                    sagaCB: {
                        onSuccess: response => {
                            setTourPreferenceData(response)
                        }
                    }
                })
            )
        }
    }, [isTokenRoute, eventId])

    useEffect(() => {
        if (tourPreferenceStatus === 'ready' && eventId) {
            setViewHelpIcon(false)
            dispatch(rootActions.tours.tourByEndpoint.onRequest({ url: currentPath }))
        }
    }, [currentPath, tourPreferenceStatus])

    useEffect(() => {
        const data = tourData?.data?.[0]

        if (!data || data.status !== 'active' || !data.steps?.length) {
            setViewHelpIcon(false)
            return
        }

        const { triggerModes = [], visibilityTypes = [], _id } = data

        setViewHelpIcon(triggerModes.includes('Help Icon'))

        if (triggerModes.includes('First Visit') || visibilityTypes.includes('New Users')) {
            const timeDelay = data?.timeDelay || 0

            if (tourPreferenceData?.data?.length) {
                // If user preference data exists and the tour hasn't been visited
                if (!tourPreferenceData.data[0]?.payload.visitedTours?.includes(_id)) {
                    // Show the tour modal after the delay and update user preferences
                    setTimeout(() => {
                        setViewTourModal(true)
                        handleEditPreferenceCustomization()
                    }, timeDelay * 1000)
                }
            } else {
                // If no preference data exists, show modal and create new preference
                setTimeout(() => {
                    setViewTourModal(true)
                    handleCreatePreferenceCustomization()
                }, timeDelay * 1000)
            }
        }
    }, [tourData])

    const handleEditPreferenceCustomization = () => {
        dispatch(
            rootActions.companies.editPreferenceCustomization.onRequest({
                data: {
                    title: 'tours',
                    module: 'tours',
                    payload: {
                        visitedTours: [...tourPreferenceData.data[0]?.payload?.visitedTours, tourData.data[0]._id]
                    }
                },
                id: tourPreferenceData.data[0]._id,
                sagaCB: {
                    onSuccess: response => {
                        setTourPreferenceData({ data: [response] })
                    }
                }
            })
        )
    }

    const handleCreatePreferenceCustomization = () => {
        dispatch(
            rootActions.companies.createPreferenceCustomization.onRequest({
                data: {
                    title: 'tours',
                    module: 'tours',
                    payload: {
                        visitedTours: [tourData.data[0]._id]
                    }
                },
                sagaCB: {
                    onSuccess: response => {
                        setTourPreferenceData({ data: [response] })
                    }
                }
            })
        )
    }

    return (
        <div className={cx(styles.container, { [styles['step-layout']]: isStepLayout })} id="support-icons">
            {viewHelpIcon && (
                <span className={styles.help}>
                    <QuestionLineIcon size={32} onClick={() => setViewTourModal(true)} />
                </span>
            )}
            {tourData?.data?.[0] && tourData.data[0]?.steps?.length && (
                <Modal
                    open={viewTourModal}
                    closable={false}
                    footer={false}
                    modalSize="large"
                    wrapClassName={styles['preview-container']}
                >
                    <Preview
                        background={tourData.data[0].bgColor || '#' + tourData.data[0].bgColorCode}
                        steps={tourData.data[0].steps}
                        showSkipButton={tourData.data[0].showSkipButton}
                        showRestartButton={tourData.data[0].showRestartButton}
                        handleClosePreview={() => setViewTourModal(false)}
                    />
                </Modal>
            )}
            <span
                onClick={() => window.open(`https://wa.me/${whatsappNumberSupport}`, '_blank', 'noopener,noreferrer')}
            >
                <WhatsappLineIcon size={32} />
            </span>
            <span className={styles.support}>
                <CustomerServiceLineIcon
                    size={32}
                    onClick={() =>
                        dispatch(
                            rootActions.ui.common.onChangeItems({
                                supportModalVisible: true,
                                userMenuVisible: false
                            })
                        )
                    }
                />
            </span>
        </div>
    )
}

export default FloatingIcons
